import { Head, useForm, usePage } from "@inertiajs/react"

import { BrandColorInput } from "@/components/business-profile/brand-color-field"
import {
  DEFAULT_HOURS,
  type DayHours,
  HoursEditor,
  type HoursStatus,
} from "@/components/business-profile/hours-editor"
import { FormField, FormFields } from "@/components/form-field"
import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsAccountPath, settingsBusinessProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface BusinessProfileData {
  business_name: string | null
  phone: string | null
  address: string | null
  website: string | null
  business_category: string | null
  description: string | null
  services: string | null
  hours: DayHours[] | null
  hours_status: HoursStatus | null
  brand_color: string | null
}

const INDUSTRY_LIST_ID = "settings-industry-options"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Settings", href: settingsAccountPath() },
]

export default function BusinessProfile() {
  const { business_profile, industry_options } = usePage<{
    business_profile: BusinessProfileData
    industry_options: string[]
  }>().props

  const { data, setData, patch, processing, recentlySuccessful } = useForm({
    business_name: business_profile.business_name ?? "",
    phone: business_profile.phone ?? "",
    address: business_profile.address ?? "",
    website: business_profile.website ?? "",
    business_category: business_profile.business_category ?? "",
    description: business_profile.description ?? "",
    services: business_profile.services ?? "",
    hours:
      business_profile.hours && business_profile.hours.length > 0
        ? business_profile.hours
        : DEFAULT_HOURS,
    hours_status: business_profile.hours_status ?? "open_no_hours",
    brand_color: business_profile.brand_color ?? "",
  })

  function updateHour(idx: number, patch: Partial<DayHours>) {
    setData(
      "hours",
      data.hours.map((h, i) => (i === idx ? { ...h, ...patch } : h)),
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(settingsBusinessProfilePath())
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Business profile" />

      <SettingsLayout>
        <form onSubmit={handleSubmit} className="space-y-10">
          <div className="space-y-6">
            <HeadingSmall
              title="Business profile"
              description="Basic information about your business used by your assistant."
            />
            <FormFields>
              <FormField label="Business name" htmlFor="business_name">
                <Input
                  id="business_name"
                  value={data.business_name}
                  onChange={(e) => setData("business_name", e.target.value)}
                  placeholder="Acme Corp"
                />
              </FormField>

              <FormField label="Phone" htmlFor="phone">
                <Input
                  id="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => setData("phone", e.target.value)}
                  placeholder="+1 (555) 000-0000"
                />
              </FormField>

              <FormField label="Address" htmlFor="address">
                <Input
                  id="address"
                  value={data.address}
                  onChange={(e) => setData("address", e.target.value)}
                  placeholder="123 Main St, New York, NY 10001"
                />
              </FormField>

              <FormField label="Website" htmlFor="website">
                <Input
                  id="website"
                  type="url"
                  value={data.website}
                  onChange={(e) => setData("website", e.target.value)}
                  placeholder="https://yourwebsite.com"
                />
              </FormField>

              <FormField label="Brand color" htmlFor="brand_color">
                <BrandColorInput
                  id="brand_color"
                  value={data.brand_color}
                  onChange={(value) => setData("brand_color", value)}
                />
              </FormField>

              <FormField
                label="Hours"
                sublabel="Set your main business hours or mark your business as closed."
              >
                <HoursEditor
                  status={data.hours_status}
                  hours={data.hours}
                  onStatusChange={(v) => setData("hours_status", v)}
                  onHourChange={updateHour}
                />
              </FormField>
            </FormFields>
          </div>

          <FormFields>
            <FormField label="Industry" htmlFor="business_category">
              <Input
                id="business_category"
                list={INDUSTRY_LIST_ID}
                value={data.business_category}
                onChange={(e) => setData("business_category", e.target.value)}
                placeholder="e.g. Plumbing, Dental practice, Law firm"
              />
              <datalist id={INDUSTRY_LIST_ID}>
                {industry_options.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </FormField>

            <FormField label="Description" htmlFor="description">
              <Textarea
                id="description"
                rows={4}
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                placeholder="Tell us what your business does…"
              />
            </FormField>

            <FormField label="Services" htmlFor="services">
              <Textarea
                id="services"
                rows={5}
                value={data.services}
                onChange={(e) => setData("services", e.target.value)}
                placeholder="List the services you offer…"
              />
            </FormField>
          </FormFields>

          <div className="flex items-center gap-4">
            <Button disabled={processing}>Save</Button>
            {recentlySuccessful && (
              <p className="text-muted-foreground text-sm">Saved</p>
            )}
          </div>
        </form>
      </SettingsLayout>
    </AppLayout>
  )
}
